import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { GetTransactionResponse } from '@/domains/transactions/types/responses/GetTransactionResponse';
import { TableSortingConfiguration } from '@/features/table/types/TableSortingConfiguration';
import { BenefitsSliceState } from './types/BenefitsSliceState';
import { SortDirection } from '@/enums/SortDirection';

const initialState: BenefitsSliceState = {
  addBenefitValue: false,
  transactionDetails: {
    byBenefitId: {},
  },
  statuses: {
    benefitTransactionsDetails: {},
  },
};

const benefitsSlice = createSlice({
  name: 'benefits',
  initialState,
  reducers: {
    createBenefit: (_, _action: PayloadAction<{ claimId: number }>) => {},
    getBenefitValue: (_, _action: PayloadAction<{ claimId: string }>) => {},
    getBenefitValueSuccess: (state, { payload: { addBenefitValue } }: PayloadAction<{ addBenefitValue: boolean }>) => {
      state.addBenefitValue = addBenefitValue;
    },
    getBenefitTransactionsDetails: (state, { payload }: PayloadAction<{ benefitId: string | number }>) => {
      const { benefitId } = payload;

      if (!state.transactionDetails.byBenefitId[benefitId]) {
        state.transactionDetails.byBenefitId[benefitId] = {
          listings: [],
          pagination: {
            numberOfItems: 0,
            first: '1',
            last: undefined,
            previous: '0',
            next: '0',
            page: 1,
            perPage: 5,
          },
          sorting: {
            sortBy: 'createdAt',
            sortDirection: SortDirection.ASC,
          },
          hideCompleteTransactions: false,
        };
      }
    },
    startLoadingBenefitTransactionsDetails: (state, { payload }: PayloadAction<{ benefitId: string | number }>) => {
      state.statuses.benefitTransactionsDetails[payload.benefitId] = {
        isLoading: true,
        isFetching: true,
        isError: false,
        isSuccess: false,
      };
    },
    getBenefitTransactionDetailsSuccess: (
      state,
      {
        payload: { benefitId, data, shouldRewrite = true },
      }: PayloadAction<{
        benefitId: string | number;
        data: GetTransactionResponse;
        shouldRewrite?: boolean;
      }>,
    ) => {
      const listingsArray = Array.isArray(data.listings) ? data.listings : [data.listings];

      const benefit = state.transactionDetails.byBenefitId[benefitId];

      if (shouldRewrite || !benefit) {
        state.transactionDetails.byBenefitId[benefitId] = {
          listings: listingsArray,
          pagination: data.pagination,
          sorting: data.sorting,
          hideCompleteTransactions: data.hideCompleteTransactions,
        };
      } else {
        state.transactionDetails.byBenefitId[benefitId] = {
          listings: [...benefit.listings, ...listingsArray],
          pagination: data.pagination,
          sorting: data.sorting,
          hideCompleteTransactions: data.hideCompleteTransactions,
        };
      }

      state.statuses.benefitTransactionsDetails[benefitId] = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    getBenefitTransactionDetailsError: (state, { payload }: PayloadAction<{ benefitId: string | number }>) => {
      state.statuses.benefitTransactionsDetails[payload.benefitId] = {
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },
    incrementBenefitTransactionsPage: (state, { payload }: PayloadAction<{ benefitId: string | number }>) => {
      const current = state.transactionDetails.byBenefitId[payload.benefitId];
      if (current) {
        current.pagination.page++;
      }
    },
    decrementBenefitTransactionsPage: (state, { payload }: PayloadAction<{ benefitId: string | number }>) => {
      const current = state.transactionDetails.byBenefitId[payload.benefitId];
      if (current && current.pagination.page > 1) {
        current.pagination.page--;
      }
    },
    setBenefitTransactionDetailsSorting: (
      state,
      { payload }: PayloadAction<{ benefitId: string | number; sorting: TableSortingConfiguration }>,
    ) => {
      const { benefitId, sorting } = payload;
      if (state.transactionDetails.byBenefitId[benefitId]) {
        state.transactionDetails.byBenefitId[benefitId].sorting = sorting;
        state.transactionDetails.byBenefitId[benefitId].pagination.page = 1;
      }
    },
    setHideCompleteBenefitTransactions: (
      state,
      { payload }: PayloadAction<{ benefitId: string | number; value: boolean }>,
    ) => {
      if (state.transactionDetails.byBenefitId[payload.benefitId]) {
        state.transactionDetails.byBenefitId[payload.benefitId].hideCompleteTransactions = payload.value;
        state.transactionDetails.byBenefitId[payload.benefitId].pagination.page = 1;
      }
    },
    duplicateBenefitTransaction(
      _,
      _action: PayloadAction<{ claimId: string | number; transactionId: string | number; benefitId: string | number }>,
    ) {},
  },
});

export const {
  createBenefit,
  getBenefitValue,
  getBenefitValueSuccess,
  getBenefitTransactionDetailsError,
  getBenefitTransactionDetailsSuccess,
  getBenefitTransactionsDetails,
  startLoadingBenefitTransactionsDetails,
  setBenefitTransactionDetailsSorting,
  setHideCompleteBenefitTransactions,
  incrementBenefitTransactionsPage,
  decrementBenefitTransactionsPage,
  duplicateBenefitTransaction,
} = benefitsSlice.actions;

export const benefitsReducer = benefitsSlice.reducer;
export const benefitsReducerPath = benefitsSlice.reducerPath;
